import type {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  IPublicClientApplication,
  PopupRequest,
  RedirectRequest,
  SilentRequest
} from "@azure/msal-browser"
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser"
import { useAccount, useIsAuthenticated, useMsal } from "@azure/msal-react"
import { useQuery } from "@tanstack/react-query"
import { createContext, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createCookieTokenProvider } from "@/shared/api/auth/tokenProvider"
import { createAuthApi } from "@/shared/api/authApi/authApi"
import type { ClaimsType } from "@/shared/api/authApi/authApi.types"
import { getPublicConfig } from "@/shared/config/config"
import { loginRequest } from "@/shared/config/msalConfig"
import { AUTH_METHOD } from "@/shared/constants/authMethods"
import { setSentryUser } from "@/shared/sentry"
import { logger } from "@/shared/utils/add/logger/logger"
import { toCamelCase } from "@/shared/utils/toCamelCase/toCamelCase"
import { redirectToLogin } from "@/utils/redirectToLogin"

type ToCamelCase<S extends string> = S extends `${infer F}_${infer R}` ? `${F}${Capitalize<ToCamelCase<R>>}` : S

export type CookieAuthUser = { [K in ClaimsType["type"] as ToCamelCase<K>]: string }

type CookieAuthState = {
  type: typeof AUTH_METHOD.COOKIE
  user: CookieAuthUser | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

type MsalClaims = Record<string, string>

type LoginHandler = (request?: PopupRequest | RedirectRequest) => Promise<void>
type LogoutHandler = (request?: EndSessionRequest) => Promise<void>

type MsalUser = {
  id: string
  username: string
  name?: string
  email?: string
  sub?: string
  claims?: MsalClaims
}

type MsalAuthState = {
  type: typeof AUTH_METHOD.MSAL
  user: MsalUser | null
  accounts: AccountInfo[]
  instance: IPublicClientApplication | null
  scopes: string[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  login: LoginHandler
  logout: LogoutHandler
  acquireTokenSilent: (request: SilentRequest) => Promise<AuthenticationResult>
  acquireTokenInteractive: (request: PopupRequest | RedirectRequest) => Promise<AuthenticationResult>
}

type AuthState = CookieAuthState | MsalAuthState

export const AuthContext = createContext<AuthState | null>(null)

function mapClaimsToUser(claims: ClaimsType[] | null): CookieAuthUser | null {
  if (!claims) {
    return null
  }

  return claims.reduce((acc, claim) => {
    acc[toCamelCase(claim.type) as keyof CookieAuthUser] = claim.value
    return acc
  }, {} as CookieAuthUser)
}

export function AuthProvider({ children }: PropsWithChildren) {
  const { instance, accounts, inProgress } = useMsal()
  const account = useAccount(accounts[0])
  const isMsalAuthenticated = useIsAuthenticated()
  const config = getPublicConfig()
  const msalScopes = useMemo(() => [config.auth.msalApiScope], [config.auth.msalApiScope])

  const [authMethod, setAuthMethod] = useState<typeof AUTH_METHOD.COOKIE | typeof AUTH_METHOD.MSAL>(() => {
    const stored = window.localStorage.getItem("authMethod")
    return stored === AUTH_METHOD.MSAL || stored === AUTH_METHOD.COOKIE ? stored : config.auth.defaultMethod
  })
  const [msalError, setMsalError] = useState<Error | null>(null)
  const [msalInitialized, setMsalInitialized] = useState(false)

  const msalUser = useMemo(() => {
    if (!account || !isMsalAuthenticated) {
      return null
    }

    const claims = account.idTokenClaims as MsalClaims | undefined

    return {
      id: account.localAccountId,
      username: account.username,
      name: account.name,
      email: account.username,
      sub: claims?.sub || account.localAccountId,
      claims
    }
  }, [account, isMsalAuthenticated])

  // Track MSAL initialization to prevent premature routing decisions
  useEffect(() => {
    if (inProgress === InteractionStatus.None && !msalInitialized) {
      // MSAL has completed its initial check (including checking for cached auth)
      setMsalInitialized(true)
    }
  }, [inProgress, msalInitialized])

  useEffect(() => {
    const syncAuthMethod = () => {
      const stored = window.localStorage.getItem("authMethod")
      if (stored === AUTH_METHOD.COOKIE || stored === AUTH_METHOD.MSAL) {
        setAuthMethod(stored)
      }
    }

    window.addEventListener("storage", syncAuthMethod)
    window.addEventListener("focus", syncAuthMethod)

    // Only auto-switch to MSAL if user is authenticated AND currently using cookie method
    // This prevents overriding explicit user choice but allows seamless MSAL usage after login
    if (accounts.length > 0 && authMethod === AUTH_METHOD.COOKIE && isMsalAuthenticated) {
      logger.debug("Auto-switching to MSAL auth method due to active MSAL session")
      setAuthMethod(AUTH_METHOD.MSAL)
      window.localStorage.setItem("authMethod", AUTH_METHOD.MSAL)
    }

    return () => {
      window.removeEventListener("storage", syncAuthMethod)
      window.removeEventListener("focus", syncAuthMethod)
    }
  }, [accounts.length, authMethod, isMsalAuthenticated])

  const login = useCallback(
    async (request?: PopupRequest | RedirectRequest) => {
      try {
        setMsalError(null)
        const loginRequestWithDefaults = { ...loginRequest, ...request }

        if (request?.redirectUri || "redirectUri" in loginRequestWithDefaults) {
          await instance.loginRedirect(loginRequestWithDefaults)
        } else {
          await instance.loginPopup(loginRequestWithDefaults)
        }

        window.localStorage.setItem("authMethod", AUTH_METHOD.MSAL)
        setAuthMethod(AUTH_METHOD.MSAL)
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Login failed", { cause: err })
        logger.error("MSAL login failed:", error)
        setMsalError(error)
        throw error
      }
    },
    [instance]
  )

  const logout = useCallback(
    async (request?: EndSessionRequest) => {
      try {
        setMsalError(null)
        const logoutRequest = {
          account: account || undefined,
          ...request
        }

        await instance.logoutPopup(logoutRequest)
        window.localStorage.setItem("authMethod", AUTH_METHOD.COOKIE)
        setAuthMethod(AUTH_METHOD.COOKIE)
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Logout failed", { cause: err })
        logger.error("MSAL logout failed:", error)
        setMsalError(error)
        throw error
      }
    },
    [instance, account]
  )

  const acquireTokenSilent = useCallback(
    async (request: SilentRequest): Promise<AuthenticationResult> => {
      try {
        setMsalError(null)
        const requestWithAccount = {
          ...request,
          account: request.account || account || accounts[0]
        }

        requestWithAccount.scopes = requestWithAccount.scopes ?? msalScopes

        if (!requestWithAccount.account) {
          throw new Error("No account available for token acquisition")
        }

        return await instance.acquireTokenSilent(requestWithAccount)
      } catch (err) {
        // Handle interaction-required scenarios from AAD (e.g., consent, MFA, expired sessions)
        if (err instanceof InteractionRequiredAuthError) {
          throw err
        }

        const error = err instanceof Error ? err : new Error("Silent token acquisition failed", { cause: err })
        logger.error("MSAL acquireTokenSilent failed:", error)
        setMsalError(error)
        throw error
      }
    },
    [instance, account, accounts, msalScopes]
  )

  const acquireTokenInteractive = useCallback(
    async (request: PopupRequest | RedirectRequest): Promise<AuthenticationResult> => {
      try {
        setMsalError(null)
        const requestWithDefaults = {
          ...request,
          scopes: request?.scopes ?? msalScopes
        }

        if (requestWithDefaults.redirectUri) {
          await instance.acquireTokenRedirect(requestWithDefaults)
          throw new Error("Redirect initiated - result will be available after redirect")
        }

        return await instance.acquireTokenPopup(requestWithDefaults)
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Interactive token acquisition failed", { cause: err })
        logger.error("MSAL acquireTokenInteractive failed:", error)
        setMsalError(error)
        throw error
      }
    },
    [instance, msalScopes]
  )

  const timerRef = useRef<number | null>(null)
  const cookieAuthApiRef = useRef(createAuthApi(createCookieTokenProvider()))

  const {
    data: cookieUserInfo,
    isLoading: isCookieLoading,
    isError: isCookieError,
    error: cookieError,
    refetch: refetchCookieUser
  } = useQuery({
    queryKey: ["userInfo", authMethod],
    queryFn:
      authMethod === AUTH_METHOD.COOKIE
        ? () => cookieAuthApiRef.current.getUserInfo()
        : async () => ({ isAuthenticated: false, claims: null, username: null, email: null }),
    enabled: authMethod === AUTH_METHOD.COOKIE
  })

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (cookieUserInfo?.isAuthenticated && cookieUserInfo.expiresAt) {
      const expirationTime = cookieUserInfo.expiresAt * 1000
      const currentTime = Date.now()
      const timeUntilExpiration = expirationTime - currentTime

      logger.debug(`Session will expire in ${Math.floor(timeUntilExpiration / 1000).toString()} seconds`)

      if (timeUntilExpiration > 0) {
        timerRef.current = window.setTimeout(() => {
          logger.debug("Session expired, redirecting to login")
          redirectToLogin()
        }, timeUntilExpiration)
      } else {
        logger.debug("Session already expired, redirecting to login")
        redirectToLogin()
      }
    }

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
      }
    }
  }, [cookieUserInfo])

  const authState: AuthState = useMemo(() => {
    if (authMethod === AUTH_METHOD.MSAL) {
      return {
        type: AUTH_METHOD.MSAL,
        user: msalUser,
        accounts,
        instance,
        scopes: msalScopes,
        isLoading: inProgress !== InteractionStatus.None || !msalInitialized,
        isError: msalError !== null,
        error: msalError,
        login,
        logout,
        acquireTokenSilent,
        acquireTokenInteractive
      }
    }

    return {
      type: AUTH_METHOD.COOKIE,
      user: cookieUserInfo?.isAuthenticated ? mapClaimsToUser(cookieUserInfo.claims) : null,
      isLoading: isCookieLoading,
      isError: isCookieError,
      error: cookieError ?? null,
      refetch: refetchCookieUser
    }
  }, [
    authMethod,
    msalUser,
    accounts,
    instance,
    msalScopes,
    inProgress,
    msalInitialized,
    msalError,
    login,
    logout,
    acquireTokenSilent,
    acquireTokenInteractive,
    cookieUserInfo,
    isCookieLoading,
    isCookieError,
    cookieError,
    refetchCookieUser
  ])

  // Sync user context with Sentry for error attribution
  useEffect(() => {
    if (authState.user) {
      const user = authState.user
      const isMsalUser = "id" in user
      setSentryUser(isMsalUser ? user.id : (user.sub ?? null))
    } else {
      setSentryUser(null)
    }
  }, [authState.user])

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
}
